import { useEffect, useState } from 'react'
import type { PartAttachmentsService } from '../parts/attachments/usePartAttachments'
import { defaultPartAttachmentsService } from '../parts/attachments/usePartAttachments'
import type { RfqLineFileView } from './domain'

/**
 * UID-528 follow-up — resolves the "Name & drawing" chip for the COMPOSE/DRAFT
 * screen's parts table (`/rfq/new`, `PartsReviewTable`).
 *
 * WHY A CLIENT-SIDE READ AT ALL. On the submitted-request DETAIL page a line is
 * an `RfqLineView` and its `files` are resolved SERVER-side by
 * `line-file-read.ts` / `part-line-file-read.ts` during `GET /rfq/{id}`. A draft
 * line is an `RfqReviewLine` — client-only, never submitted, never round-tripped
 * through that endpoint — so `files` does not exist on it and there is no server
 * projection to read. The chip therefore has to be resolved from the browser, off
 * the SAME durable Parts Library attachments the server projection reads:
 * `GET /parts/{partId}/attachments` (UID-152, already real, already the seam
 * `usePartAttachments` uses). NO new endpoint and NO contract change is needed —
 * this deliberately adds neither.
 *
 * `partId` IS `line.sourceable.id`. That is the same assumption `RfqCard.tsx` and
 * the server-side resolver already make, for every line kind
 * (catalog/custom/configurable): a draft line's sourceable id is the part id, and
 * is what the download route is keyed by.
 *
 * DISCLOSED SIMPLIFICATION vs the server rule. `part-line-file-read.ts` applies a
 * two-step precedence: (1) the newest `library.part_authored_revision`'s explicit
 * `drawing_attachment_id` FK wins outright, else (2) recency over
 * `library.part_attachment` rows of kind `technical_drawing`. Step (1) is NOT
 * reachable from the browser: there is no GET on `/parts/{partId}/revisions` in the
 * contract (POST only — it is an authoring endpoint), and nothing else projects
 * `drawingAttachmentId`. So this hook implements step (2) ONLY. The consequence is
 * narrow and stated rather than silent: for a part whose newest authored revision
 * points at an OLDER drawing than a later stray upload, the draft screen names the
 * later upload where the detail page would name the revision's. Both are real,
 * tenant-owned drawings on that same part — never a fabricated one — and closing
 * the gap properly means a read projection for authored revisions, which is a
 * contract change and therefore a separate ticket, not a quiet divergence here.
 *
 * FAILURE POSTURE. A part with no drawing, an unreadable part, or a failed read
 * yields NO entry, so the caller renders NO chip. A missing chip is never worth
 * interrupting the compose flow with an error — the customer did not ask for this
 * read and cannot act on its failure. (A failed CLICK on a chip that IS shown does
 * surface a toast; that one the customer did ask for. See `openRfqLineFile`.)
 */

/**
 * Canonical-UUID guard, mirroring `part-line-file-read.ts`'s identical
 * precondition: a real Library part id is a `uuid` (both `library.part_attachment`
 * and `library.part_authored_revision` key on `uuid` columns, so the server-side
 * resolver filters non-UUIDs before reading rather than eating a 22P02). Some
 * demo/fixture sourceables legitimately carry non-UUID ids (e.g. `'src-bracket'`,
 * `'part-1'`); those can only ever 404 here, so they are filtered BEFORE the read —
 * no wasted round trip per row, and the row renders a clean "no chip" instead of a
 * swallowed error.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `PartAttachmentList` is documented newest-first, so the FIRST
 * `technical_drawing` row is the newest one — matching the server resolver's
 * `records.find((r) => r.fileKind === fileKind)` over its own newest-first list.
 * At most ONE drawing chip per part, for the same reason: a part with three
 * uploaded revisions of a drawing must not render three chips. */
const DRAWING_FILE_KIND = 'technical_drawing'

export function useDraftLineDrawings(
  partIds: readonly string[],
  service: PartAttachmentsService = defaultPartAttachmentsService,
): ReadonlyMap<string, RfqLineFileView> {
  const [drawings, setDrawings] = useState<ReadonlyMap<string, RfqLineFileView>>(() => new Map())

  // De-duped (several draft lines can reference the SAME part — one read, not
  // one per row), UUID-filtered, and sorted into a single primitive so the effect
  // below re-runs on a real change to the SET of parts and not merely on a new
  // array identity every render (adding a line, editing a quantity, or toggling a
  // checkbox all re-render this table).
  const key = [...new Set(partIds)]
    .filter((id) => UUID_RE.test(id))
    .sort()
    .join(',')

  useEffect(() => {
    const ids = key === '' ? [] : key.split(',')
    if (ids.length === 0) {
      setDrawings((prev) => (prev.size === 0 ? prev : new Map()))
      return
    }
    let cancelled = false
    // Concurrent, not sequential: the reads are independent, single indexed
    // lookups, and one slow part must not stall every other row's chip.
    void Promise.all(
      ids.map(async (partId): Promise<readonly [string, RfqLineFileView] | null> => {
        try {
          const attachments = await service.listAttachments(partId)
          const newest = attachments.find((a) => a.fileKind === DRAWING_FILE_KIND)
          return newest === undefined
            ? null
            : [partId, { name: newest.filename, kind: 'drawing', id: newest.id }]
        } catch {
          // Best-effort per part — see FAILURE POSTURE above. One unreadable
          // part never denies the other rows their chips.
          return null
        }
      }),
    ).then((entries) => {
      if (cancelled) return
      setDrawings(
        new Map(
          entries.filter((entry): entry is readonly [string, RfqLineFileView] => entry !== null),
        ),
      )
    })
    return () => {
      cancelled = true
    }
  }, [key, service])

  return drawings
}
